import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';

import { GatewayActions, GatewayBrand, GatewayContent, GatewayShell } from '@/_shared/components/GatewayShell';
import { Button } from '@/_shared/components/ui/button';

import type { RudderstackAccessDenial } from '../contexts/WorkspaceContext';
import { useAuth } from '../hooks/useAuth';
import { clearLastWorkspaceId, getLastWorkspaceId } from '../lib/last-workspace';
import { orpc } from '../lib/orpc';

/**
 * ACT2-1124 §5.7: the RudderStack membership check denies this sibling workspace. The text comes from the server.
 * "Try again" reads `auth.me` again, which checks again; it shows only when the check itself failed.
 */
export function RudderstackAccessDenied({
  deniedSlugs,
  denial,
  canOpenAnother,
}: {
  /** This workspace and the person's other denied workspaces. */
  deniedSlugs: readonly string[];
  denial: RudderstackAccessDenial;
  /** The person has another workspace that the check does not deny. */
  canOpenAnother: boolean;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const { signOutAndRedirect } = useAuth();
  const retry = useMutation({
    mutationFn: async () => queryClient.refetchQueries({ queryKey: orpc.auth.me.key() }),
  });
  const checkFailed = denial.reason === 'check_failed';

  return (
    <GatewayShell>
      <GatewayBrand poweredBy />
      <GatewayContent
        icon={<ShieldAlert />}
        variant="warning"
        title="You cannot open this workspace"
        description={denial.message}
      />
      <GatewayActions>
        {checkFailed && (
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            loading={retry.isPending}
            onClick={() => retry.mutate()}
          >
            Try again
          </Button>
        )}
        {canOpenAnother && (
          <Button variant={checkFailed ? 'secondary' : 'primary'} size="lg" className="w-full" asChild>
            <Link
              to="/"
              state={{ pickWorkspace: true }}
              onClick={() => {
                // Otherwise the home page opens this workspace, or another denied one, again.
                const lastId = getLastWorkspaceId();
                if (lastId && deniedSlugs.includes(lastId)) clearLastWorkspaceId();
              }}
            >
              Switch to another workspace
            </Link>
          </Button>
        )}
        {/* Always present, so a person with no other workspace is not stuck on this page. */}
        <Button
          variant="text"
          className="text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          onClick={() => void signOutAndRedirect()}
        >
          Sign in with a different account
        </Button>
      </GatewayActions>
    </GatewayShell>
  );
}
